import {Component, Input, OnChanges, OnInit} from '@angular/core';

const GOLDEN_GATE = '#F13939';

// Pantry wordmark color per theme bucket. Keep in sync with pantry-foundations CSS when themes update.
const THEME_TINT: {[bucket: string]: string} = {
  'golden-gate': GOLDEN_GATE, // red — default bucket
  baker: '#C2B3A4',           // stone
  presidio: '#7AC142',        // green
  sutro: '#009FE0',           // blue
  fortpoint: '#D4693A',       // orange
};

// Banners per theme bucket. Mirrors server.js:getStyleThemeByBanner — keep in sync when banners
// are added or moved between buckets.
const BANNERS_BY_BUCKET: {[bucket: string]: string[]} = {
  'golden-gate': ['safeway', 'vons', 'carrsqc', 'jewelosco', 'randalls', 'tomthumb', 'acmemarket'],
  baker: ['pavilions'],
  presidio: ['andronicos', 'shaws', 'haggen', 'starmarket', 'shopamigos'],
  fortpoint: ['balduccis', 'shopmarketstreet'],
  sutro: ['albertsons', 'kingsfoodmarkets', 'shopunitedsupermarkets', 'shopalbertsonsmarket'],
};

// Inverted banner → bucket lookup derived from BANNERS_BY_BUCKET (source of truth above).
const BUCKET_BY_BANNER: {[banner: string]: string} = {};
Object.keys(BANNERS_BY_BUCKET).forEach(bucket => {
  BANNERS_BY_BUCKET[bucket].forEach(banner => (BUCKET_BY_BANNER[banner] = bucket));
});

/**
 * Renders the FORU+ logo using pantry's single B4U+ artwork class,
 * `pds-svg-foru-plus-vertical-color-yellow-tm` (pantry-foundations >= 2.1.20260902-mr-350).
 *
 * The artwork is NOT stored in this library and is no longer inlined as markup — pantry draws it
 * entirely in CSS from the class applied in the template: `::before` is a mask of the wordmark
 * painted with a solid background-color, and `::after` is the gold plus-badge. This component only
 * sizes the box and re-points the wordmark color at the active banner's tint (see the scss).
 *
 * The page must load the pantry theme CSS (server.js:getStyleThemeByBanner); with it absent
 * (e.g. bare ng serve) the box renders empty.
 *
 * The consuming app must pass [banner] to drive the wordmark tint. Falls back to
 * window['AB']?.userInfo?.banner (standard AEM global) when the input is not provided.
 *
 * MANUAL TEST OVERRIDE: append `?mockLogoBanner=<banner>` to the URL to force a specific tint
 * for the logo only (e.g. ?mockLogoBanner=albertsons). Inert in production.
 */
@Component({
  selector: 'foru-plus-logo',
  templateUrl: './foru-plus-logo.component.html',
  styleUrls: ['./foru-plus-logo.component.scss']
})
export class ForuPlusLogoComponent implements OnInit, OnChanges {
  @Input() banner = '';
  // CSS height for the rendered logo (width tracks it — pantry's vertical artwork is square).
  @Input() height = '100%';
  @Input() ariaLabel = 'ForU+';
  // When true the logo is purely decorative (host provides the accessible name, e.g. a wrapping
  // link), so it is hidden from assistive tech to avoid a duplicate announcement.
  @Input() decorative = false;

  tint = GOLDEN_GATE;

  ngOnInit(): void {
    this.render();
  }

  ngOnChanges(): void {
    this.render();
  }

  private render(): void {
    const banner = this.resolveBanner();
    this.tint = (banner && THEME_TINT[BUCKET_BY_BANNER[banner]]) || GOLDEN_GATE;
  }

  // Priority: ?mockLogoBanner= URL param → @Input() banner → window['AB'] AEM global → null.
  private resolveBanner(): string | null {
    const mock = new URLSearchParams(window.location.search).get('mockLogoBanner');
    if (mock) return mock;
    if (this.banner) return this.banner;
    return window['AB']?.['userInfo']?.['banner'] || null;
  }
}
